import {
  type InfiniteData,
  keepPreviousData,
  type QueryKey,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { buildListParams, type DataGridQuery, type PageRequest } from "@/hooks/use-data-grid";
import { getJson } from "@/lib/api-client";
import type { AlbumGroup } from "@/lib/enums";
import { projectKeys } from "@/lib/query";
import {
  type PhotoListResponse,
  photoListResponseSchema,
  projectDetailResponseSchema,
  projectListResponseSchema,
} from "@/schemas/photos";

export function useProjects(query: DataGridQuery, pageRequest: PageRequest) {
  return useQuery({
    queryKey: projectKeys.list(query, `${pageRequest.direction}:${pageRequest.cursor}`),
    queryFn: () =>
      getJson(`/api/projects?${buildListParams(query, pageRequest)}`, projectListResponseSchema),
    placeholderData: keepPreviousData,
  });
}

export function useProject(projectId: string) {
  return useQuery({
    queryKey: projectKeys.detail(projectId),
    queryFn: async () =>
      (await getJson(`/api/projects/${encodeURIComponent(projectId)}`, projectDetailResponseSchema))
        .data,
  });
}

export interface PhotoFilters {
  album?: string;
  group?: AlbumGroup;
  from?: string;
  to?: string;
  company?: string;
  make?: string;
  model?: string;
  serial?: string;
  /** Words in the AI description, a condition tag or the equipment. */
  search?: string;
  /** "Needs a retake": photos with an open AI flag other than near_duplicate. PM and admin only. */
  retake?: boolean;
  order: "desc" | "asc";
}

/** The filters the way the server's photo list takes them (no order; "Needs a retake" is `retake=1`). */
export function photoListFilters({ order: _order, retake, ...rest }: PhotoFilters) {
  return { ...rest, ...(retake ? { retake: "1" as const } : {}) };
}

export function usePhotos(projectId: string, filters: PhotoFilters) {
  return useInfiniteQuery<
    PhotoListResponse,
    Error,
    InfiniteData<PhotoListResponse>,
    QueryKey,
    string | null
  >({
    queryKey: projectKeys.photos(projectId, filters),
    initialPageParam: null,
    getNextPageParam: (last) => (last.hasNext ? last.nextCursor : null),
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ sort: "capturedAt", sortDir: filters.order });
      for (const [key, value] of Object.entries(photoListFilters(filters))) {
        if (value) params.set(key, value);
      }
      if (pageParam) params.set("cursor", pageParam);
      return getJson(
        `/api/projects/${encodeURIComponent(projectId)}/photos?${params}`,
        photoListResponseSchema,
      );
    },
  });
}
